<template>
    <div class="create-edit-ph">
        <div class="create-edit-ph__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div class="create-edit-ph__wrapper">
                        <div class="create-edit-ph__title">{{ router.currentRoute.value.params.id === 'create' ? $t('salesRep.createPH') : router.currentRoute.value.params.id === 'create-rks' ? $t('salesRep.createRKS') : currentRole === ROLE_ID_PH ? $t('salesRep.editPH') : $t('salesRep.editRKS') }}</div>
                        <div class="create-edit-ph__close-wrapper kasanowa-hover" @click="navigateTo(!isAdminWholesaler ? '/dashboard' : `/warehouse/details/${userInfo.wholesaler_ids[0]}`)">
                            <KIcon icon="close" :color="black" width="25" height="25" />
                        </div>
                        <KLoader v-show="loadingWarehouse || loadingSalesRepDetails" />
                        <Form v-show="!loadingWarehouse && !loadingSalesRepDetails" :validation-schema="schema" @submit="onSubmit" class="create-edit-ph__form-wrapper">
                            <div class="create-edit-ph__input-wrapper">
                                <KTextField
                                    id="input-name"
                                    v-model="salesRepForm.name"
                                    name="name"
                                    label=""
                                    :placeholder="$t('salesRep.nameForm')"
                                />
                                <KTextField
                                    v-if="(currentRole == null || currentRole === ROLE_ID_PH) && router.currentRoute.value.params.id !== 'create-rks'"
                                    id="input-id-asseco"
                                    v-model="salesRepForm.idAsseco"
                                    name="idAsseco"
                                    label=""
                                    :placeholder="$t('salesRep.idAssecoForm')"
                                />
                            </div>
                            <div class="create-edit-ph__input-wrapper">
                                <KTextField
                                    id="input-last-name"
                                    v-model="salesRepForm.lastName"
                                    name="lastName"
                                    label=""
                                    :placeholder="$t('salesRep.lastNameForm')"
                                />
                                <KTextField
                                    id="input-email"
                                    v-model="salesRepForm.email"
                                    name="email"
                                    label=""
                                    :placeholder="$t('salesRep.emailForm')"
                                />
                            </div>
                            <div v-if="!isPh" class="create-edit-ph__autocomplete-wrapper">
                                <KAutocomplete
                                    id="select-warehouse-list"
                                    v-model="salesRepForm.warehousesIds"
                                    name="warehouseList"
                                    :get-items="warehouseListItems"
                                    item-title="name"
                                    item-value="id"
                                    label=""
                                    :placeholder="$t('salesRep.warehouse')"
                                    variant="outlined"
                                    clearable
                                    :multiple="salesRepForm.isSalesReg"
                                    :disabled="isAdminWholesaler"
                                />
                            </div>
                            <div class="create-edit-ph__btn-wrapper">
                                <KButton
                                    id="submit-btn"
                                    :label="$t('salesRep.save')"
                                    type="submit"
                                    icon="arrowRight"
                                    :color="black"
                                    width="11"
                                    height="11"
                                    :disabled="loading"
                                    :loading="loading"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from '@vue/runtime-core';
import router from '@/router';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import WarehouseApi from '@/api/v1/WarehouseApi';
import SalesRepApi from '@/api/v1/SalesRepApi';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { toast } from 'vue3-toastify';
import { navigateTo, navigateToWithSuccessToast, navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import { ROLE_ID_PH, ROLE_ID_PH_REG } from '@/hooks/UserHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { ISalesRepForm } from '@/models/SalesRepModel';
import type { IWareHouseItems } from '@/models/WarehouseModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import KTextField from '@/components/atoms/KTextField.vue';
import KButton from '@/components/atoms/KButton.vue';
import KAutocomplete from '@/components/atoms/KAutocomplete.vue';
import KLoader from '@/components/atoms/KLoader.vue';
import KIcon from '@/components/atoms/KIcon.vue';

const { black } = variables;
const { isPh, isAdminWholesaler, userInfo } = storeToRefs(useUserStore());

const warehouseApi = new WarehouseApi();
const loadingWarehouse = ref<boolean>(false);
const warehouseListItems = ref<IWareHouseItems[]>([]);
const warehousesList = async() => {
    loadingWarehouse.value = true;
    try {
        const result = await warehouseApi.warehouseList(
            1,
            100,
            null,
            null,
            null,
        );
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorWarehouseList'));
            loadingWarehouse.value = false;
            return;
        }
        warehouseListItems.value = result.payload.data;
    } catch (e) {
        toast.error(t('salesRep.toast.errorWarehouseList'));
        loadingWarehouse.value = false;
        return;
    }
    loadingWarehouse.value = false;
};
warehousesList();

const salesRepForm = ref<ISalesRepForm>({
    name: null,
    idAsseco: null,
    lastName: null,
    email: null,
    warehousesIds: isAdminWholesaler.value && userInfo.value.wholesaler_ids.length > 0 ? userInfo.value.wholesaler_ids[0] : null,
    isSalesReg: router.currentRoute.value.params.id === 'create-rks',
});
let schema:any = yup.object({
    name: yup.string().required(`${t('salesRep.validation.nameRequired')}`).label('name'),
    idAsseco: yup.string().required(`${t('salesRep.validation.idAssecoRequired')}`).label('idAsseco'),
    lastName: yup.string().required(`${t('salesRep.validation.lastNameRequired')}`).label('lastName'),
    email: yup.string().required(`${t('salesRep.validation.emailRequired')}`).email(`${t('salesRep.validation.email')}user@example.pl`).label('email'),
    warehouseList: yup.number().required(`${t('salesRep.validation.warehouseRequired')}`).label('warehouseList'),
});
if (isPh.value) {
    schema = yup.object({
        name: yup.string().required(`${t('salesRep.validation.nameRequired')}`).label('name'),
        idAsseco: yup.string().required(`${t('salesRep.validation.idAssecoRequired')}`).label('idAsseco'),
        lastName: yup.string().required(`${t('salesRep.validation.lastNameRequired')}`).label('lastName'),
        email: yup.string().required(`${t('salesRep.validation.emailRequired')}`).email(`${t('salesRep.validation.email')}user@example.pl`).label('email'),
    });
}
if (router.currentRoute.value.params.id === 'create-rks') {
    schema = yup.object({
        name: yup.string().required(`${t('salesRep.validation.nameRequired')}`).label('name'),
        lastName: yup.string().required(`${t('salesRep.validation.lastNameRequired')}`).label('lastName'),
        email: yup.string().required(`${t('salesRep.validation.emailRequired')}`).email(`${t('salesRep.validation.email')}user@example.pl`).label('email'),
        warehouseList: yup.number().required(`${t('salesRep.validation.warehouseRequired')}`).label('warehouseList'),
    });
}
const loading = ref<boolean>(false);
const salesRepApi = new SalesRepApi();
const onSubmit = async() => {
    loading.value = true;
    if (router.currentRoute.value.params.id === 'create' || router.currentRoute.value.params.id === 'create-rks') {
        try {
            const result = await salesRepApi.createSalesRep({
                name: salesRepForm.value.name,
                surname: salesRepForm.value.lastName,
                assecoId: salesRepForm.value.idAsseco,
                email: salesRepForm.value.email,
                warehouse_ids: Array.isArray(salesRepForm.value.warehousesIds) ? salesRepForm.value.warehousesIds : [salesRepForm.value.warehousesIds],
                isSalesReg: router.currentRoute.value.params.id === 'create-rks' ? true : salesRepForm.value.isSalesReg,
                role: salesRepForm.value.isSalesReg ? ROLE_ID_PH_REG : ROLE_ID_PH,
            });
            if (!result.isSuccess) {
                toast.error(t('salesRep.toast.errorCreateSalesRep'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.successCreateSalesRep'));
        } catch (e) {
            toast.error(t('salesRep.toast.errorCreateSalesRep'));
            loading.value = false;
            return;
        }
    } else {
        try {
            const result = await salesRepApi.editSalesRep(
                {
                    name: salesRepForm.value.name,
                    surname: salesRepForm.value.lastName,
                    assecoId: salesRepForm.value.idAsseco,
                    email: salesRepForm.value.email,
                    warehouse_ids: Array.isArray(salesRepForm.value.warehousesIds) ? salesRepForm.value.warehousesIds : [salesRepForm.value.warehousesIds],
                    isSalesReg: salesRepForm.value.isSalesReg,
                    role: salesRepForm.value.isSalesReg ? ROLE_ID_PH_REG : ROLE_ID_PH,
                },
                router.currentRoute.value.params.id,
            );
            if (!result.isSuccess) {
                toast.error(t('salesRep.toast.errorEditSalesRep'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.successEditSalesRep'));
        } catch (e) {
            toast.error(t('salesRep.toast.errorEditSalesRep'));
            loading.value = false;
            return;
        }
    }
    loading.value = false;
};

const loadingSalesRepDetails = ref<boolean>(false);
const currentRole = ref<string | null>(null);
const salesRepDetails = async() => {
    loadingSalesRepDetails.value = true;
    try {
        const result = await salesRepApi.salesRepDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.errorSalesRepDetails'))
            loadingSalesRepDetails.value = false;
            return;
        }
        currentRole.value = result.payload.role;
        if (currentRole.value !== ROLE_ID_PH) {
            schema = yup.object({
                name: yup.string().required(`${t('salesRep.validation.nameRequired')}`).label('name'),
                lastName: yup.string().required(`${t('salesRep.validation.lastNameRequired')}`).label('lastName'),
                email: yup.string().required(`${t('salesRep.validation.emailRequired')}`).email(`${t('salesRep.validation.email')}user@example.pl`).label('email'),
                warehouseList: yup.number().required(`${t('salesRep.validation.warehouseRequired')}`).label('warehouseList'),
            });
        }
        salesRepForm.value.name = result.payload.name;
        salesRepForm.value.idAsseco = result.payload.asseco_id;
        salesRepForm.value.lastName = result.payload.surname;
        salesRepForm.value.email = result.payload.email;
        salesRepForm.value.isSalesReg = result.payload.role === ROLE_ID_PH_REG;
        setTimeout(() => {
            if (result.payload.role === ROLE_ID_PH && result.payload.warehouses.length > 0) {
                salesRepForm.value.warehousesIds = parseInt(result.payload.warehouses[0].id);
            }
            if (result.payload.role === ROLE_ID_PH_REG && result.payload.warehouses.length > 0) {
                salesRepForm.value.warehousesIds = result.payload.warehouses.map((elem: any) => parseInt(elem.id));
            }
        }, 1);
    } catch (e) {
        navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('salesRep.toast.errorSalesRepDetails'))
        loadingSalesRepDetails.value = false;
        return;
    }
    loadingSalesRepDetails.value = false;
};
if (router.currentRoute.value.params.id !== 'create' && router.currentRoute.value.params.id !== 'create-rks') {
    salesRepDetails();
}

watch(() => salesRepForm.value.isSalesReg, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        salesRepForm.value.warehousesIds = null;

        if (!newValue) {
            schema.fields.warehouseList.type = 'number';
        } else {
            schema.fields.warehouseList.type = 'array';
        }
    }
}, {
    deep: true,
});
</script>

<style scoped lang="scss">
.create-edit-ph {
    position: relative;
    width: 100%;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__wrapper {
        position: relative;
        background: rgba($white, 0.15);
        backdrop-filter: blur(30px) saturate(150%);
        -webkit-backdrop-filter: blur(30px) saturate(150%);
        border-radius: 5px;
        border: 1px solid $secondary-400;
        padding: 45px 15px 15px 15px;
        max-width: 1025px;
        margin: 20px auto 0;

        @include media-breakpoint-up(lg) {
            padding: 124px 52px 60px 52px;
            margin: 50px auto 0;
        }
    }

    &__title {
        width: 90%;
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $primary-400;
        text-transform: uppercase;
        font-weight: 900;
        letter-spacing: 0;
        text-align: center;
        padding: 10px;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 9px;
            top: -50px;
        }
    }

    &__form-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__input-wrapper {
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            gap: 16px;
        }

        &:deep(.k-input .v-field__input) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__autocomplete-wrapper {
        &:deep(.k-autocomplete .v-field) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__btn-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            justify-content: flex-end;
        }
    }

    &__close-wrapper {
        position: absolute;
        top: 18px;
        right: 12px;

        @include media-breakpoint-up(sm) {
            top: 12px;
            right: 12px;
        }
    }
}
</style>
